import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { tap, catchError, map } from 'rxjs/operators';
import { of, throwError } from 'rxjs';
import {
  AddSms,
  GetBorrowerSmsHistory,
  GetSmsVariables,
  ClearSmsStore,
  SubscribeToBorrowerTexts,
  GetFullBorrowerSmsHistory,
  GetUnknownSms,
  DeleteSmsFromUnknownSender,
  AddBorrowerToSms,
  SmsAlertDismissed, MarkSmsRead, MarkSmsUnread
} from './sms.actions';
import { Sms } from '../sms-service/sms.model';
import { SmsService } from '../sms-service/sms.service';
import { PusherService } from 'app/modules/shared/services/pusher.service';
import { Injectable } from '@angular/core';
import { Alert } from 'ng2-app/components/alert/alert.interface';
import { UnknownInboxItemPaginationData } from 'app/modules/task-management/models/unknown-inbox-item/unknown-inbox-item-pagination-data.model';
import { SmsVariables } from '../sms-service/sms-variables.model';
import { NotifySmsThreadRead, NotifySmsThreadUnread, NotifyUnknownSmsRead, NotifyUnknownSmsUnread } from 'ng2-app/modules/today/state/sms/user-sms.action';
import { UpdateCounts } from 'ng2-app/modules/today/state/today-badge-counts/today-badge-counts.action';

export class SmsStateModel {
    sms: Sms[];
    unknownSms: Sms[];
    loadingInitial: boolean;
    loadingAll: boolean;
    alert: null | Alert;
    unknownSmsNextPageUrl: null | string;
    unknownSmsCurrentPage: number;
    unknownSmsLastPage: number;
    smsVariables: SmsVariables | any;
}

@State<SmsStateModel>({
    name: 'sms',
    defaults: {
        sms: [],
        unknownSms: [],
        loadingInitial: false,
        loadingAll: false,
        alert: null,
        unknownSmsNextPageUrl: null,
        unknownSmsCurrentPage: 0,
        unknownSmsLastPage: 0,
        smsVariables: null,
    }
})

@Injectable()
export class SmsState {

    @Selector()
    static sms(state: SmsStateModel) {
        return state.sms;
    }

    @Selector()
    static unknownSms(state: SmsStateModel) {
        return state.unknownSms;
    }

    @Selector()
    static alert(state: SmsStateModel) {
        return state.alert;
    }

    @Selector()
    static paginationData(state: SmsStateModel): UnknownInboxItemPaginationData {
      return {
        currentPage: state.unknownSmsCurrentPage,
        lastPage: state.unknownSmsLastPage,
        nextPageUrl: state.unknownSmsNextPageUrl
      };
    }

    @Selector()
    static loadingInitial(state: SmsStateModel) {
      return state.loadingInitial;
    }

    constructor(
        private smsService: SmsService,
        private pusherService: PusherService,
        private store: Store
    ) { }

    @Action(AddSms)
    addSms({ patchState }: StateContext<SmsStateModel>, { sms }: AddSms) {
        patchState({ alert: null });
        return this.smsService.save(sms).pipe(catchError(() => {
            // error logged on api side
            // error response from api isn't helpful
            // display generic message
            patchState({
                alert: {
                    level: 'error',
                    message: 'Something went wrong. Please try again.'
                }
            });
            return of();
        }));
    }

    @Action(GetBorrowerSmsHistory)
    getBorrowerSmsHistory({ patchState }: StateContext<SmsStateModel>, { borrowerId }: GetBorrowerSmsHistory) {
        patchState({ loadingInitial: true });

        return this.smsService.getBorrowerSmsHistory(borrowerId).pipe(tap(sms => {
            patchState({ sms, loadingInitial: false });
        }));
    }

    @Action(GetFullBorrowerSmsHistory)
    getFullBorrowerSmsHistory({ patchState }: StateContext<SmsStateModel>, { borrowerId }: GetBorrowerSmsHistory) {
        patchState({ loadingAll: true });

        return this.smsService.getBorrowerSmsHistory(borrowerId, true).pipe(tap(sms => {
            patchState({ sms, loadingAll: false });
        }));
    }

    @Action(GetSmsVariables)
    getSmsVariables({ patchState }: StateContext<SmsStateModel>, { borrowerId }: GetSmsVariables) {
        return this.smsService.getSmsVariables(borrowerId).pipe(catchError(err => throwError(() => err)),
            map(smsVariables => {
                patchState({ smsVariables });
            }));
    }

    @Action(SubscribeToBorrowerTexts)
    subscribeToBorrowerTexts(_: StateContext<SmsStateModel>, { borrowerId }: SubscribeToBorrowerTexts) {
        this.pusherSubscribe(borrowerId);
        this.store.dispatch(new GetBorrowerSmsHistory(borrowerId));
    }

    @Action(ClearSmsStore)
    clearSmsStore({ patchState }: StateContext<SmsStateModel>, { borrowerId }: ClearSmsStore) {
        this.pusherUnsubscribe(borrowerId);
        patchState({ sms: [] });
    }

    @Action(GetUnknownSms)
    getUnknownThreads({ getState, patchState }: StateContext<SmsStateModel>, { lenderUserId }: GetUnknownSms) {
        patchState({ loadingInitial: true });

        const {unknownSmsCurrentPage, unknownSmsLastPage, unknownSmsNextPageUrl, unknownSms} = getState();

        if (unknownSmsCurrentPage && unknownSmsCurrentPage >= unknownSmsLastPage) {
          return;
        }

        return this.smsService.getUnknownSms(lenderUserId, unknownSmsNextPageUrl || null).pipe(tap(unknownSmsResponse => {
            patchState({
              unknownSms: [...unknownSms, ...unknownSmsResponse.data],
              unknownSmsCurrentPage: unknownSmsResponse.current_page,
              unknownSmsLastPage: unknownSmsResponse.last_page,
              unknownSmsNextPageUrl: unknownSmsResponse.next_page_url,
              loadingInitial: false
            });
        }));
    }

    @Action(DeleteSmsFromUnknownSender)
    deleteMessageFromUnknown({ patchState, getState }: StateContext<SmsStateModel>,
        { smsId }: DeleteSmsFromUnknownSender) {
        return this.smsService.delete(smsId).pipe(
            catchError(() => {
                patchState({
                    alert: {
                        level: 'error',
                        message: 'Failed to delete sms.'
                    }
                });
                this.dismissDelay();
                return of();
            }), tap(() => {
                const state = getState();
                patchState({
                    unknownSms: state.unknownSms.filter(sms => sms.id !== smsId),
                    alert: {
                        level: 'success',
                        message: 'Successfully deleted sms.'
                    }
                });
            })
        );

    }

    @Action(AddBorrowerToSms)
    addBorrowerToMessage({ getState, patchState }: StateContext<SmsStateModel>, { smsId, borrower }: AddBorrowerToSms) {
        const update = { borrowerId: borrower.id };
        return this.smsService.put(smsId, update).pipe(catchError(() => {
            patchState({
                alert: {
                    level: 'error',
                    message: 'Failed to save sms to ' + borrower.name
                }
            });
            return of();
        }), tap(() => {
            const state = getState();
            patchState({
                unknownSms: state.unknownSms.filter(sms => sms.id !== smsId),
                alert: {
                    level: 'success',
                    message: 'Sms saved to ' + borrower.name
                }
            });
        }));
    }

    @Action(SmsAlertDismissed)
    messageAlertDismissed({ patchState }: StateContext<SmsStateModel>) {
        patchState({ alert: null });
    }

    @Action(MarkSmsRead)
    markSmsRead(
      {}: StateContext<SmsStateModel>,
      {lenderUserId, smsIds, borrowerId}: MarkSmsRead
    ) {
      return this.smsService.markSmsRead(lenderUserId, borrowerId, smsIds).pipe(
        catchError((err) => {
            if(err !== 'FORBIDDEN') {
                throw err;
            }
            return of();
        }),
        map((newCounts) => {
          if(borrowerId) {
            this.store.dispatch( new NotifySmsThreadRead(borrowerId) );
          } else {
            this.store.dispatch( new NotifyUnknownSmsRead(smsIds[0]) );
          }

          this.store.dispatch( new UpdateCounts(newCounts) );
        })
      );
    }

    @Action(MarkSmsUnread)
    MarkSmsUnread(
      {}: StateContext<SmsStateModel>,
      {lenderUserId, smsIds, borrowerId}: MarkSmsUnread
    ) {
      return this.smsService.markSmsUnread(lenderUserId, borrowerId, smsIds).pipe(
        catchError((err) => {
          throw err;
        }),
        map((newCounts) => {
          if(borrowerId) {
            this.store.dispatch( new NotifySmsThreadUnread(borrowerId, newCounts.unreadSmsCountForThread) );
          } else {
            this.store.dispatch( new NotifyUnknownSmsUnread() );
          }

          this.store.dispatch( new UpdateCounts(newCounts) );
        })
      );
    }

    pusherSubscribe(borrowerId: number) {
        this.pusherService
            .subscribe(`private-borrower-${borrowerId}-texts`)
            .bind_global(this.handlePusherEvent.bind(this, borrowerId));
    }

    handlePusherEvent(borrowerId: number, eventName: string) {
        if (eventName.startsWith('text-')) {
            this.store.dispatch(new GetBorrowerSmsHistory(borrowerId));
        }
    }

    pusherUnsubscribe(borrowerId: number) {
        this.pusherService.unsubscribe(`private-borrower-${borrowerId}-texts`);
    }

    dismissDelay() {
        setTimeout(() => {
            this.store.dispatch(new SmsAlertDismissed());
        }, 5000);
    }
}


